/** Conservative RoomPilot name matching: spelling/order only, never substring/fuzzy matching.
 * Rate-only suffixes do not describe a room. Bed, view, size and repeated counts stay significant.
 * Shared with the quick-quote client so the gallery and grouping use the same boundary.
 */
export function roomNameKey(name: string): string {
  const folded = name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/ı/g, 'i').toLowerCase();
  if (folded.includes('+')) return `combination:${folded.replace(/\s+/g, ' ').trim()}`;
  const clean = folded.replace(/\bnon[ -]?refundable\b/g, '').replace(/\biadesiz\b/g, '');
  const tokens = (clean.match(/\p{N}+(?:[.,]\p{N}+)+|[\p{L}\p{N}]+/gu) ?? []).map(word => word.replace(',', '.'));
  const counts = new Set(['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'single', 'double', 'triple', 'quadruple', 'bir', 'iki', 'uc', 'dort', 'bes', 'alti', 'yedi', 'sekiz', 'dokuz', 'on']);
  // Counts qualify their following noun. Keep both order AND room/bed nouns in these names.
  // Non-Latin names also stay ordered: we do not guess number words in other languages.
  if (tokens.some(word => /\p{N}/u.test(word) || counts.has(word) || /[^a-z]/.test(word))) return tokens.join(' ');
  const words = tokens.filter(word => !['room', 'rooms', 'bed', 'beds', 'oda', 'odasi', 'odalar', 'with', 'and', 've', 'ile', 'the'].includes(word));
  return words.sort().join(' ');
}

export function matchedRoom<T extends { name: string }>(rooms: readonly T[], name: string): T | null {
  const key = roomNameKey(name);
  if (!key) return null;
  const matches = rooms.filter(room => roomNameKey(room.name) === key);
  // Multiple catalogue identities are ambiguous even when their normalized titles match.
  return matches.length === 1 ? matches[0] ?? null : null;
}
